import React, { useEffect, useState } from 'react';
import { buildStyles, CircularProgressbar } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import styled from 'styled-components';
import { Text } from 'components';
import { tablet, useQuery } from 'styles/breakpoints';

const LoaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3.5rem 0;
  max-width: 28.75rem;
  width: 100%;
  position: relative;
  z-index: 3;
  @media ${tablet} {
    max-width: unset;
    padding: 2.25rem 1rem;
  }
`;

const ImageWrapper = styled.div`
  width: 15rem;
  height: 15rem;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  position: relative;
  @media ${tablet} {
    width: 11.5rem;
    height: 11.5rem;
  }
`;

const Title = styled(Text)`
  color: #fff;
  text-align: center;
  font-size: 1.75rem;
  font-style: normal;
  font-weight: 700;
  line-height: 2.0625rem;
  margin-bottom: 2rem;
  @media ${tablet} {
    margin-bottom: 1.25rem;
    font-size: 1.125rem;
    line-height: 2rem;
  }
`;

const StyledPercent = styled(Text)`
  position: absolute;
  color: #f7c800;
  text-align: center;
  font-size: 2.375rem;
  font-style: normal;
  font-weight: 700;
  line-height: 150%;
  letter-spacing: -0.02613rem;
  @media ${tablet} {
    font-size: 2.375rem;
    line-height: 150%;
    letter-spacing: -0.02613rem;
  }
`;

const LOADING_DURATION = 6000; // Total loading duration in milliseconds
const INTERVAL = LOADING_DURATION / 100; // Interval for updating the progress

interface CircularProgressBarProps {
  onComplete: () => void;
  title?: string;
}

const CircularProgressBar = ({
  onComplete,
  title,
}: CircularProgressBarProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [progress, setProgress] = useState(0);

  const { isTablet } = useQuery();

  useEffect(() => {
    const animationInterval = setInterval(() => {
      setProgress(prevProgress =>
        prevProgress >= 100 ? 100 : prevProgress + 1,
      );
    }, INTERVAL); // Update interval for the animation

    const timer = setTimeout(() => {
      setIsLoading(false);
      clearInterval(animationInterval);
    }, LOADING_DURATION); // Timer for the total loading duration

    return () => {
      clearTimeout(timer);
      clearInterval(animationInterval);
    };
  }, []);

  useEffect(() => {
    if (!isLoading) {
      const completeTimer = setTimeout(() => {
        onComplete();
      }, 1000);

      return () => clearTimeout(completeTimer);
    }
  }, [isLoading, onComplete]);

  return (
    <LoaderContainer>
      <Title textAlign="center">{title}</Title>
      <div>
        <ImageWrapper>
          <CircularProgressbar
            value={progress}
            text=""
            styles={buildStyles({
              pathTransitionDuration: 0.25,
              pathColor: '#F7C800',
              trailColor: '#F0F3FB99',
            })}
          />
          <StyledPercent>{progress}%</StyledPercent>
        </ImageWrapper>
      </div>
    </LoaderContainer>
  );
};

export default CircularProgressBar;
